/**
 * The What's new card's door to the server and its read (DECISIONS 120). The
 * seen state is ONE document the viewer already may read,
 * users/{uid}/messageInboxState/bulletin__hub-whats-new, written only by
 * netlify/functions/whatsNewSeen.mjs on every sign-in the person holds. See
 * netlify/functions/lib/whatsNewSeen.mjs for why it lives there.
 *
 * READ THROUGH THE LISTENER THE APP ALREADY HOLDS (refute round 3). That doc
 * sits in the collection every signed-in home already lists in full through
 * useMessageInboxStates (AppBadgeSync, mounted for every session but a plain
 * dancer's, and the homes' own unread checks). A second listener on the doc
 * itself billed it twice on every home open and on every Got it. So the card
 * calls the SAME hook with the same uid: Firestore shares one listen target
 * between identical queries, the doc is billed once, inside a listener that
 * was open anyway, and the card adds no target of its own.
 * No read-budget test pins the card yet; it adds no listen target of its own.
 */
import { useEffect, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { db } from './firebase';
import { functionHeaders } from './functionSecurity';
import { messageInboxStateKey, useMessageInboxStates } from './messageInboxState';
import {
  WHATS_NEW_STATE_DOC_ID,
  WHATS_NEW_STATE_ITEM_ID,
  WHATS_NEW_STATE_KIND,
  parseSeenIds,
  studioDateKey,
  type WhatsNewSeenState,
} from './whatsNew';

export interface WhatsNewStateResult {
  /** True until the first answer. */
  loading: boolean;
  /** No state doc yet: the card asks the server to start one. */
  missing: boolean;
  state: WhatsNewSeenState;
  error: string | null;
}

const EMPTY: WhatsNewSeenState = { startDate: null, seenIds: new Set(), clearedOn: null };
const STATE_KEY = messageInboxStateKey(WHATS_NEW_STATE_KIND, WHATS_NEW_STATE_ITEM_ID);

export function useWhatsNewState(uid: string | null | undefined): WhatsNewStateResult {
  // No uid, no listener: the hook is handed undefined and opens nothing.
  const { states, loading, error } = useMessageInboxStates(uid ?? undefined);
  // The uid the listener above has been told about. On the one render where
  // uid has just changed, its effect has not run yet, so it still reports the
  // OLD uid's answer (often "not loading, nothing stored"). Reading that as
  // "missing" would send the card's start request for nothing; until this
  // catches up, the answer is "still loading". Both effects run in the same
  // flush, so the next render already sees the listener loading.
  const [armedUid, setArmedUid] = useState<string | null>(null);
  useEffect(() => { setArmedUid(uid ?? null); }, [uid]);
  if (!db || !uid) return { loading: false, missing: false, state: EMPTY, error: null };
  if (armedUid !== uid) return { loading: true, missing: false, state: EMPTY, error: null };
  if (error) return { loading: false, missing: false, state: EMPTY, error };
  if (loading) return { loading: true, missing: false, state: EMPTY, error: null };
  const stored = states.get(STATE_KEY);
  if (!stored || stored.id !== WHATS_NEW_STATE_DOC_ID) return { loading: false, missing: true, state: EMPTY, error: null };
  // readThroughMs arrives already guarded by useMessageInboxStates: a finite
  // number of milliseconds, or null.
  const startMs = stored.readThroughMs;
  // filedThroughMs is when Got it was last tapped on any of her sign-ins (the
  // lane keeps the latest): a tap this month keeps the card away until the 1st.
  const clearedMs = stored.filedThroughMs;
  return {
    loading: false,
    missing: false,
    state: {
      startDate: startMs === null ? null : studioDateKey(new Date(startMs)),
      seenIds: parseSeenIds(stored.readThroughContent),
      clearedOn: clearedMs === null ? null : studioDateKey(new Date(clearedMs)),
    },
    error: null,
  };
}

export async function postWhatsNewSeen(body: { action: 'start' } | { action: 'clear'; ids: string[] }): Promise<void> {
  const me = getAuth().currentUser;
  if (!me) throw new Error('sign_in_required');
  const response = await fetch('/.netlify/functions/whatsNewSeen', {
    method: 'POST',
    credentials: 'same-origin',
    cache: 'no-store',
    headers: await functionHeaders(me),
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(payload.error ?? 'server_error');
  }
}
